/**
 * Public nomination form — anyone can propose a person to be added to the
 * World Changers directory. Submissions land in the admin review queue.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const CATEGORY_OPTIONS = [
  "Peace & Human Rights", "Humanitarian Aid", "Medicine & Healthcare",
  "Science", "Environment & Conservation", "Education", "Technology & Innovation",
  "Social Entrepreneurship", "Philanthropy", "Civil Rights", "Women's Rights",
  "Children's Rights", "Space", "Arts & Culture", "Journalism",
  "Faith & Service", "Public Service", "Climate Action", "Animal Welfare",
  "Clean Water", "Housing", "Business for Good", "Disability Advocacy",
];

export default function WorldChangerNominateModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [nationality, setNationality] = useState("");
  const [occupation, setOccupation] = useState("");
  const [tagline, setTagline] = useState("");
  const [reason, setReason] = useState("");
  const [referenceUrl, setReferenceUrl] = useState("");
  const [wikiUrl, setWikiUrl] = useState("");
  const [cats, setCats] = useState([]);
  const [submitterName, setSubmitterName] = useState("");
  const [submitterEmail, setSubmitterEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const toggleCat = (c) => {
    setCats((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c].slice(0, 5));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Please enter the person's name");
    if (reason.trim().length < 20) return toast.error("Please write a bit more about why they should be included (min 20 chars)");
    setSubmitting(true);
    try {
      await http.post("/world-changers/nominate", {
        name: name.trim(),
        tagline: tagline.trim() || null,
        nationality: nationality.trim() || null,
        occupation: occupation.trim() || null,
        categories: cats,
        reason: reason.trim(),
        reference_url: referenceUrl.trim() || null,
        wiki_url: wikiUrl.trim() || null,
        submitter_name: submitterName.trim() || null,
        submitter_email: submitterEmail.trim() || null,
      });
      toast.success("Nomination submitted — thank you! An editor will review shortly.");
      onClose();
      // Reset
      setName(""); setNationality(""); setOccupation(""); setTagline("");
      setReason(""); setReferenceUrl(""); setWikiUrl(""); setCats([]);
      setSubmitterName(""); setSubmitterEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="nominate-modal-backdrop"
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-[#14213D]/25 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="nominate-modal"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-[color:var(--wofy-bg)] shadow-2xl border"
        style={{ borderColor: "var(--wofy-border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--wofy-border)" }}>
          <div>
            <div className="overline">NOMINATE</div>
            <h2 className="font-display text-2xl mt-1">Propose a World Changer</h2>
          </div>
          <button data-testid="nominate-close" onClick={onClose} className="wofy-btn-ghost" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4 text-sm">
          <div>
            <label className="block mb-1 font-mono text-xs uppercase tracking-wider">Full name *</label>
            <input
              data-testid="nominate-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              placeholder="e.g. Vandana Shiva"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-mono text-xs uppercase tracking-wider">Nationality</label>
              <input
                data-testid="nominate-nationality"
                value={nationality}
                onChange={(e) => setNationality(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                placeholder="e.g. Indian"
              />
            </div>
            <div>
              <label className="block mb-1 font-mono text-xs uppercase tracking-wider">Primary role</label>
              <input
                data-testid="nominate-occupation"
                value={occupation}
                onChange={(e) => setOccupation(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                placeholder="e.g. Environmental activist"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-mono text-xs uppercase tracking-wider">Short tagline</label>
            <input
              data-testid="nominate-tagline"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              maxLength={500}
              className="w-full px-3 py-2 rounded-lg border bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              placeholder="e.g. Founder of Navdanya, seed sovereignty pioneer"
            />
          </div>

          <div>
            <label className="block mb-1 font-mono text-xs uppercase tracking-wider">Areas of impact (pick up to 5)</label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORY_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`nominate-cat-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                  onClick={() => toggleCat(c)}
                  className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${cats.includes(c) ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
                  style={cats.includes(c) ? { background: "var(--wofy-primary)", color: "#FDFBF7", borderColor: "var(--wofy-primary)" } : { borderColor: "var(--wofy-border)" }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block mb-1 font-mono text-xs uppercase tracking-wider">Why should they be included? *</label>
            <textarea
              data-testid="nominate-reason"
              required
              minLength={20}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              placeholder="A short paragraph on their impact, projects, and legacy…"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-mono text-xs uppercase tracking-wider">Wikipedia link</label>
              <input
                data-testid="nominate-wiki"
                type="url"
                value={wikiUrl}
                onChange={(e) => setWikiUrl(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                placeholder="https://en.wikipedia.org/wiki/…"
              />
            </div>
            <div>
              <label className="block mb-1 font-mono text-xs uppercase tracking-wider">Other reference</label>
              <input
                data-testid="nominate-reference"
                type="url"
                value={referenceUrl}
                onChange={(e) => setReferenceUrl(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                placeholder="Website, foundation, or article link"
              />
            </div>
          </div>

          <div className="pt-4 border-t" style={{ borderColor: "var(--wofy-border)" }}>
            <div className="overline mb-2">Your details (optional)</div>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                data-testid="nominate-submitter-name"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                placeholder="Your name"
              />
              <input
                data-testid="nominate-submitter-email"
                type="email"
                value={submitterEmail}
                onChange={(e) => setSubmitterEmail(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                placeholder="Your email (for follow-up)"
              />
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost">Cancel</button>
            <button
              type="submit"
              data-testid="nominate-submit"
              disabled={submitting}
              className="wofy-btn-primary"
            >
              {submitting ? "Submitting…" : "Submit nomination"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
